import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import PortalLayout from '../components/PortalLayout';
import { Badge } from '../components/ui/badge';
import { Flask, Users, CaretDown, CaretRight, ArrowLeft, Clock, UserList, FileText, ClipboardText, Circle, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const visitColorMap = {
  green: 'bg-green-500',
  yellow: 'bg-amber-400',
  red: 'bg-red-500',
  gray: 'bg-stone-300',
};
const visitColorBorder = {
  green: 'border-green-200 bg-green-50',
  yellow: 'border-amber-200 bg-amber-50',
  red: 'border-red-200 bg-red-50',
  gray: 'border-stone-200 bg-stone-50',
};
const visitColorText = {
  green: 'text-green-700',
  yellow: 'text-amber-700',
  red: 'text-red-600',
  gray: 'text-stone-500',
};

// Stand-by: code coloring of volunteer visits is disabled until reactivated.
// Set to true to re-enable green/yellow/red status (already computed by backend).
const VISIT_COLOR_CODING_ENABLED = false;
const visitColor = (c) => (VISIT_COLOR_CODING_ENABLED ? c : 'gray');

export default function PortalStudyDetail() {
  const { lang } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedVol, setExpandedVol] = useState(null);
  const [rightTab, setRightTab] = useState('equipo');

  const lb = lang === 'es' ? {
    back: 'Volver al panel', loading: 'Cargando...', notFound: 'Estudio no encontrado',
    conclusion: 'Progreso Estudio', recruited: 'Reclutados', excluded: 'Excluidos',
    t0: 'Visitas T0', intermediate: 'V. intermedias', final: 'Visitas finales',
    startWeek: 'Semana de inicio', volunteers: 'Voluntarios',
    dropouts: 'Abandonos',
    noVolunteers: 'No hay voluntarios incluidos', noVisits: 'Sin visitas configuradas',
    equipo: 'Equipo', documentos: 'Documentos', protocolo: 'Protocolo', ea: 'E. Adversos',
    noStaff: 'No hay personal asignado', noDocuments: 'Proximamente',
    noProtocol: 'Sin datos de protocolo', role: 'Puesto', name: 'Nombre',
    activo: 'Activo', finalizado: 'Finalizado',
    legendCompleted: 'Completado', legendPartial: 'Parcial', legendPending: 'Pendiente',
    noAdverse: 'Sin eventos adversos', volunteerCode: 'Voluntario', visitCode: 'Visita',
    question: 'Pregunta', observation: 'Observacion', date: 'Fecha',
    studyInfo: 'Datos del estudio', code: 'Codigo', type: 'Tipo', phase: 'Fase',
    status: 'Estado', dates: 'Fechas',
  } : {
    back: 'Back to dashboard', loading: 'Loading...', notFound: 'Study not found',
    conclusion: 'Study Progress', recruited: 'Recruited', excluded: 'Excluded',
    t0: 'Initial (T0)', intermediate: 'Intermediate', final: 'Final visits',
    startWeek: 'Start week', volunteers: 'Volunteers',
    dropouts: 'Dropouts',
    noVolunteers: 'No included volunteers', noVisits: 'No visits configured',
    equipo: 'Team', documentos: 'Documents', protocolo: 'Protocol', ea: 'Adverse E.',
    noStaff: 'No staff assigned', noDocuments: 'Coming soon',
    noProtocol: 'No protocol data', role: 'Role', name: 'Name',
    activo: 'Active', finalizado: 'Completed',
    legendCompleted: 'Completed', legendPartial: 'Partial', legendPending: 'Pending',
    noAdverse: 'No adverse events', volunteerCode: 'Volunteer', visitCode: 'Visit',
    question: 'Question', observation: 'Observation', date: 'Date',
    studyInfo: 'Study data', code: 'Code', type: 'Type', phase: 'Phase',
    status: 'Status', dates: 'Dates',
  };

  const roleLabels = {
    investigador_principal: 'Investigador Principal',
    director_estudio: 'Director Estudio',
    monitor: 'Monitor', coordinador: 'Coordinador',
    data_manager: 'Data Manager', sondas: 'Sondas',
    reclutamiento: 'Reclutamiento', investigador: 'Co-Investigador',
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/studies/${id}`, { withCredentials: true })
      .then(r => setStudy(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PortalLayout><div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" /></div></PortalLayout>;
  if (!study) return <PortalLayout><div className="text-center py-20"><Flask size={48} className="text-stone-300 mx-auto mb-3" /><p className="text-sm text-stone-500">{lb.notFound}</p></div></PortalLayout>;

  const k = study.kpis || {};
  const statusColors = { activo: 'bg-green-50 text-green-700 border-green-200', finalizado: 'bg-stone-100 text-stone-600 border-stone-200' };

  const bars = [
    { label: lb.recruited, pct: k.recruited_pct || 0, color: 'bg-[#1f3e80]' },
    { label: lb.excluded, pct: k.excluded_pct || 0, color: 'bg-red-500' },
    { label: lb.t0, pct: k.t0_pct || 0, color: 'bg-green-500' },
    { label: lb.intermediate, pct: k.intermediate_pct || 0, color: 'bg-amber-500' },
    { label: lb.final, pct: k.final_pct || 0, color: 'bg-emerald-600' },
  ];

  const tabs = [
    { id: 'equipo', label: lb.equipo, icon: UserList },
    { id: 'documentos', label: lb.documentos, icon: FileText },
    { id: 'protocolo', label: lb.protocolo, icon: ClipboardText },
    { id: 'ea', label: lb.ea, icon: Warning },
  ];

  return (
    <PortalLayout>
      <div data-testid="portal-study-detail">
        <button onClick={() => navigate('/portal')} className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1f3e80] transition-colors mb-4" data-testid="portal-back-btn">
          <ArrowLeft size={14} /> {lb.back}
        </button>

        <div className="flex flex-col lg:flex-row gap-5">
          {/* ═══ LEFT PANEL: Volunteers ═══ */}
          <div className="lg:w-[45%] space-y-3">
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <h2 className="text-base font-heading font-semibold text-stone-900 mb-1">{study.title}</h2>
              <div className="flex items-center gap-2 text-xs text-stone-500">
                <span className="font-mono">{study.code}</span>
                <Badge variant="outline" className={`text-[10px] ${statusColors[study.status] || ''}`}>{lb[study.status] || study.status}</Badge>
              </div>
            </div>

            {/* Legend (hidden while color coding is in stand-by) */}
            {VISIT_COLOR_CODING_ENABLED && (
              <div className="flex items-center gap-4 text-[10px] text-stone-500 px-1">
                <span className="flex items-center gap-1"><Circle size={8} weight="fill" className="text-green-500" />{lb.legendCompleted}</span>
                <span className="flex items-center gap-1"><Circle size={8} weight="fill" className="text-amber-400" />{lb.legendPartial}</span>
                <span className="flex items-center gap-1"><Circle size={8} weight="fill" className="text-red-500" />{lb.legendPending}</span>
              </div>
            )}

            {/* Volunteer list */}
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden max-h-[65vh] overflow-y-auto">
              {(!study.volunteer_list || study.volunteer_list.length === 0) ? (
                <p className="text-xs text-stone-400 text-center py-8">{lb.noVolunteers}</p>
              ) : study.volunteer_list.map(vol => (
                <div key={vol.code} className="border-b border-stone-100 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setExpandedVol(expandedVol === vol.code ? null : vol.code)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-stone-50 text-left transition-colors"
                    data-testid={`portal-vol-${vol.code}`}
                  >
                    {expandedVol === vol.code ? <CaretDown size={14} className="text-stone-400" /> : <CaretRight size={14} className="text-stone-400" />}
                    <span className="text-sm font-mono font-semibold text-[#1f3e80]">{vol.code}</span>
                    {/* Mini color dots summary */}
                    <div className="flex items-center gap-0.5 ml-auto">
                      {vol.visits.map((v, i) => (
                        <div key={i} className={`w-2 h-2 rounded-full ${visitColorMap[visitColor(v.color)]}`} title={v.code} />
                      ))}
                    </div>
                  </button>
                  {expandedVol === vol.code && (
                    <div className="px-4 pb-3 space-y-1">
                      {vol.visits.length === 0 ? (
                        <p className="text-[11px] text-stone-400 pl-6">{lb.noVisits}</p>
                      ) : vol.visits.map((v, i) => (
                        <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded border text-xs ${visitColorBorder[visitColor(v.color)]}`}>
                          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${visitColorMap[visitColor(v.color)]}`} />
                          <span className={`font-mono font-semibold ${visitColorText[visitColor(v.color)]}`}>{v.code}</span>
                          <span className="text-stone-500 truncate">{v.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ═══ RIGHT PANEL: KPIs + Info + Tabs ═══ */}
          <div className="lg:w-[55%] space-y-3">
            {/* KPI bars */}
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-stone-500">{lb.conclusion}</span>
                <span className="text-lg font-bold font-mono text-[#1f3e80]">{study.timeline_pct}%</span>
              </div>
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-[#1f3e80] rounded-full" style={{ width: `${study.timeline_pct}%` }} />
              </div>
              {study.start_week && (
                <p className="text-[11px] text-stone-500 mb-3">{lb.startWeek}: <span className="text-[#1f3e80] font-mono font-semibold">{study.start_week}</span></p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {bars.map(b => (
                  <div key={b.label}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[10px] text-stone-500 truncate">{b.label}</span>
                      <span className="text-[11px] font-mono font-semibold text-stone-700 ml-1">{b.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${b.color}`} style={{ width: `${b.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between" data-testid="portal-dropouts">
                <span className="text-[11px] text-stone-500 flex items-center gap-1.5">
                  <Warning size={12} className="text-amber-500" /> {lb.dropouts}
                </span>
                <span className="text-sm font-mono font-semibold text-amber-700" data-testid="portal-dropouts-count">{k.dropouts || 0}</span>
              </div>
            </div>

            {/* Study info */}
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <h3 className="text-xs font-semibold text-stone-700 mb-2">{lb.studyInfo}</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                <div><span className="text-stone-400">{lb.code}:</span> <span className="text-stone-700 font-mono">{study.code}</span></div>
                <div><span className="text-stone-400">{lb.type}:</span> <span className="text-stone-700">{study.product_type}</span></div>
                <div><span className="text-stone-400">{lb.phase}:</span> <span className="text-stone-700">{study.phase || '-'}</span></div>
                <div><span className="text-stone-400">{lb.status}:</span> <span className="text-stone-700">{lb[study.status] || study.status}</span></div>
                <div className="col-span-2"><span className="text-stone-400">{lb.dates}:</span> <span className="text-stone-700">{study.start_date || '-'}{study.end_date ? ` — ${study.end_date}` : ''}</span></div>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
              <div className="flex border-b border-stone-200">
                {tabs.map(t => {
                  const Icon = t.icon;
                  const showAeBadge = t.id === 'ea' && (k.new_adverse_events || 0) > 0;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={async () => {
                        setRightTab(t.id);
                        if (t.id === 'ea' && (k.new_adverse_events || 0) > 0) {
                          // auto-acknowledge
                          try {
                            await axios.post(`${API_URL}/api/portal/studies/${id}/adverse-events/acknowledge`, {}, { withCredentials: true });
                            setStudy(prev => prev ? {
                              ...prev,
                              kpis: { ...prev.kpis, new_adverse_events: 0 },
                              adverse_effects: (prev.adverse_effects || []).map(ev => ({ ...ev, is_new: false })),
                            } : prev);
                          } catch (_e) { /* ignore */ }
                        }
                      }}
                      data-testid={`portal-subtab-${t.id}`}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-b-2 relative ${
                        rightTab === t.id ? 'border-[#1f3e80] text-[#1f3e80]' : 'border-transparent text-stone-500 hover:text-stone-700'
                      }`}
                    >
                      <Icon size={14} /> {t.label}
                      {showAeBadge && (
                        <span data-testid="portal-ea-tab-badge" className="absolute top-1 right-2 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold">
                          {k.new_adverse_events}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="p-4 max-h-[40vh] overflow-y-auto">
                {rightTab === 'equipo' && (
                  <div>
                    {(!study.staff || study.staff.length === 0) ? (
                      <p className="text-xs text-stone-400 text-center py-4">{lb.noStaff}</p>
                    ) : (
                      <div className="space-y-2">
                        {study.staff.map((s, i) => (
                          <div key={i} className="flex items-center gap-3 p-2.5 bg-stone-50 rounded">
                            <div className="w-7 h-7 rounded-full bg-[#1f3e80] text-white flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                              {s.name[0]}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-stone-800">{s.name}</p>
                              <p className="text-[11px] text-stone-500">{roleLabels[s.role] || s.role}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {rightTab === 'documentos' && (
                  <p className="text-xs text-stone-400 text-center py-8">{lb.noDocuments}</p>
                )}

                {rightTab === 'protocolo' && (
                  <div>
                    {(!study.protocol || study.protocol.length === 0) ? (
                      <p className="text-xs text-stone-400 text-center py-4">{lb.noProtocol}</p>
                    ) : (
                      <div className="space-y-2">
                        {study.protocol.map((p, i) => (
                          <div key={i} className="border-b border-stone-100 pb-2 last:border-b-0">
                            <p className="text-[11px] font-medium text-stone-500">{p.label}</p>
                            <p className="text-sm text-stone-800">{p.value}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {rightTab === 'ea' && (
                  <div>
                    {(!study.adverse_effects || study.adverse_effects.length === 0) ? (
                      <p className="text-xs text-stone-400 text-center py-4">{lb.noAdverse}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-stone-50">
                            <tr>
                              <th className="text-left p-2 font-medium text-stone-600">{lb.volunteerCode}</th>
                              <th className="text-left p-2 font-medium text-stone-600">{lb.visitCode}</th>
                              <th className="text-left p-2 font-medium text-stone-600">{lb.question}</th>
                              <th className="text-left p-2 font-medium text-stone-600">{lb.observation}</th>
                              <th className="text-left p-2 font-medium text-stone-600">{lb.date}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {study.adverse_effects.map((ae, i) => (
                              <tr key={i} className="border-t border-stone-100">
                                <td className="p-2 font-mono font-semibold text-stone-700">{ae.volunteer_code}</td>
                                <td className="p-2 text-stone-600">{ae.visit_code}</td>
                                <td className="p-2 text-stone-600 max-w-[150px] truncate">{ae.question}</td>
                                <td className="p-2 text-stone-500">{ae.observation || '-'}</td>
                                <td className="p-2 text-stone-400">{ae.date ? ae.date.substring(0, 10) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
